<template>
  <component
    :is="isClickable ? 'RouterLink' : 'div'"
    class="PlayerAvatar"
    :class="{
      'PlayerAvatar--hasImage': !!avatarUrl && isImageLoaded,
      'PlayerAvatar--isPlaceholder': !modelValue && !isPlayerLoading,
      'PlayerAvatar--isLoading': isPlayerLoading,
      'PlayerAvatar--isHighlighted': isHighlighted,
    }"
    :style="computedTheme && `
      --themeColorPrimary: var(--themeColorPrimary${computedTheme});
      --themeColorSecondary: var(--themeColorSecondary${computedTheme});
    `"
    :data-testid="modelValue ? 'playerAvatar' : 'playerPlaceholder'"
    :data-player-id="modelValue?.id"
    :data-variant="variant"
    :data-size="size"
    :aria-busy="!isImageLoaded"
    :to="isClickable ? playerRoute || {} : undefined"
    @click.stop.prevent="handleClick"
  >
    <div class="PlayerAvatar__container">
      <div class="PlayerAvatar__separator" />
      <div class="PlayerAvatar__background" />

      <Transition>
        <div
          v-if="modelValue && !avatarUrl && isImageLoaded"
          class="PlayerAvatar__abbreviationContainer"
        >
          <abbr
            class="PlayerAvatar__abbreviation"
            :title="modelValue.name"
          >
            {{ abbreviation }}
          </abbr>
        </div>
      </Transition>
      <div
        v-if="modelValue && avatarUrl"
        class="PlayerAvatar__imageContainer"
      >
        <img
          class="PlayerAvatar__image"
          loading="lazy"
          :width="imageSize"
          :height="imageSize"
          :src="avatarUrl"
          :alt="`${modelValue.name} profile picture`"
          @loadstart="isImageLoaded = false"
          @load="isImageLoaded = true"
          @error="handleImageError"
        >
      </div>

      <div class="PlayerAvatar__border" />
    </div>

    <svg
      v-if="hasAward"
      class="PlayerAvatar__awardIcon"
      data-testid="awardIcon"
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.75 18.499C15.1641 18.499 15.4998 18.835 15.5 19.249C15.5 19.6632 15.1642 19.999 14.75 19.999H7.25C6.83579 19.999 6.5 19.6632 6.5 19.249C6.50018 18.835 6.8359 18.499 7.25 18.499H14.75ZM17.75 2C18.9921 2.00136 19.9986 3.00798 20 4.25V8.75C19.9964 10.8001 18.3478 12.468 16.2979 12.4951C15.6412 13.753 14.5618 14.7392 13.25 15.2803V16.999H8.75V15.2803C7.4382 14.7392 6.35879 13.753 5.70215 12.4951C3.65217 12.468 2.00357 10.8001 2 8.75V4.25C2.00136 3.00798 3.00792 2.00136 4.25 2H17.75ZM4.24902 3.49902C3.83513 3.49967 3.49956 3.83512 3.49902 4.24902V8.74805C3.50234 9.75021 4.16943 10.6296 5.13379 10.9023C5.04881 10.5135 5.00372 10.1167 4.99902 9.71875V3.49902H4.24902ZM17.001 3.49902V9.71875C16.9963 10.1167 16.9512 10.5136 16.8662 10.9023C17.8306 10.6296 18.4976 9.75023 18.501 8.74805V4.24902C18.5004 3.83509 18.1649 3.49962 17.751 3.49902H17.001ZM10.9951 4.97363C10.8224 4.97429 10.6697 5.08509 10.6152 5.24902L10.1074 6.78125H8.58984C8.41887 6.78115 8.26629 6.88938 8.20996 7.05078C8.15374 7.21227 8.20582 7.39188 8.33984 7.49805L9.56055 8.46387L9.0459 10.0742C8.99268 10.2416 9.05447 10.4245 9.19824 10.5254C9.34201 10.6263 9.53454 10.6216 9.67383 10.5146L10.999 9.49805L12.3174 10.5146C12.4566 10.6217 12.6491 10.6261 12.793 10.5254C12.9369 10.4246 12.9985 10.2417 12.9453 10.0742L12.4316 8.46484L13.6592 7.49902C13.7936 7.39311 13.847 7.21349 13.791 7.05176C13.735 6.89004 13.5823 6.78135 13.4111 6.78125H11.8916L11.377 5.24707C11.322 5.08359 11.1686 4.97361 10.9961 4.97363H10.9951Z"
        fill="var(--themeColorNeutral900)"
      />
      <path
        d="M15.5 19.249H17.5V19.2482L15.5 19.249ZM6.5 19.249L4.5 19.2482V19.249H6.5ZM17.75 2L17.7522 0H17.75V2ZM20 4.25H22L22 4.24781L20 4.25ZM20 8.75L22 8.75349V8.75H20ZM16.2979 12.4951L16.2714 10.4953L15.0774 10.5111L14.5249 11.5696L16.2979 12.4951ZM13.25 15.2803L12.4874 13.4314L11.25 13.9417V15.2803H13.25ZM13.25 16.999V18.999H15.25V16.999H13.25ZM8.75 16.999H6.75V18.999H8.75V16.999ZM8.75 15.2803H10.75V13.9417L9.5126 13.4314L8.75 15.2803ZM5.70215 12.4951L7.47511 11.5696L6.92255 10.5111L5.72861 10.4953L5.70215 12.4951ZM2 8.75H-3.09944e-06L2.98023e-06 8.75348L2 8.75ZM2 4.25L0 4.24782V4.25H2ZM4.25 2V-1.19209e-06L4.24782 1.19209e-06L4.25 2ZM4.24902 3.49902V1.49902L4.24589 1.49903L4.24902 3.49902ZM3.49902 4.24902L1.49902 4.24643V4.24902H3.49902ZM3.49902 8.74805H1.49901L1.49903 8.75466L3.49902 8.74805ZM5.13379 10.9023L4.58951 12.8269L7.80007 13.7349L7.08767 10.4753L5.13379 10.9023ZM4.99902 9.71875H2.99902V9.73055L2.99916 9.74236L4.99902 9.71875ZM4.99902 3.49902H6.99902V1.49902H4.99902V3.49902ZM17.001 3.49902V1.49902H15.001V3.49902H17.001ZM17.001 9.71875L19.0008 9.74239L19.001 9.73057V9.71875H17.001ZM16.8662 10.9023L14.9123 10.4753L14.2 13.7347L17.4104 12.8269L16.8662 10.9023ZM18.501 8.74805L20.501 8.7547V8.74805H18.501ZM18.501 4.24902H20.501L20.501 4.24643L18.501 4.24902ZM17.751 3.49902L17.7539 1.49902H17.751V3.49902ZM10.9951 4.97363V2.97362L10.9875 2.97365L10.9951 4.97363ZM10.6152 5.24902L8.71716 4.61869L8.71678 4.61984L10.6152 5.24902ZM10.1074 6.78125V8.78125H11.5516L12.0059 7.41044L10.1074 6.78125ZM8.58984 6.78125L8.58863 8.78125H8.58984V6.78125ZM8.20996 7.05078L6.32164 6.3918L6.32117 6.39316L8.20996 7.05078ZM8.33984 7.49805L7.09795 9.06575L7.09889 9.06649L8.33984 7.49805ZM9.56055 8.46387L11.4656 9.07271L11.887 7.75425L10.8015 6.89542L9.56055 8.46387ZM9.0459 10.0742L7.14082 9.46538L7.13994 9.46815L9.0459 10.0742ZM9.19824 10.5254L8.04951 12.1626L8.04953 12.1626L9.19824 10.5254ZM9.67383 10.5146L8.4565 8.92779L8.45572 8.92839L9.67383 10.5146ZM10.999 9.49805L12.2203 7.91424L11.0022 6.97492L9.78169 7.91119L10.999 9.49805ZM12.3174 10.5146L11.0961 12.0985L11.0984 12.1003L12.3174 10.5146ZM12.793 10.5254L13.9401 12.1637L13.9404 12.1635L12.793 10.5254ZM12.9453 10.0742L14.8515 9.46882L14.8506 9.46609L12.9453 10.0742ZM12.4316 8.46484L11.1949 6.89303L10.1044 7.75106L10.5263 9.07297L12.4316 8.46484ZM13.6592 7.49902L14.8959 9.07084L14.8967 9.07016L13.6592 7.49902ZM13.791 7.05176L15.6811 6.3978L15.6808 6.39689L13.791 7.05176ZM13.4111 6.78125L13.4123 4.78125H13.4111V6.78125ZM11.8916 6.78125L9.99545 7.41733L10.453 8.78125H11.8916V6.78125ZM11.377 5.24707L13.2731 4.61099L13.2728 4.61001L11.377 5.24707ZM10.9961 4.97363V6.97363H10.9963L10.9961 4.97363ZM14.75 18.499V20.499C14.0587 20.499 13.5003 19.9389 13.5 19.2499L15.5 19.249L17.5 19.2482C17.4994 17.7311 16.2696 16.499 14.75 16.499V18.499ZM15.5 19.249H13.5C13.5 18.5587 14.0596 17.999 14.75 17.999V19.999V21.999C16.2688 21.999 17.5 20.7678 17.5 19.249H15.5ZM14.75 19.999V17.999H7.25V19.999V21.999H14.75V19.999ZM7.25 19.999V17.999C7.94039 17.999 8.5 18.5587 8.5 19.249H6.5H4.5C4.5 20.7678 5.73118 21.999 7.25 21.999V19.999ZM6.5 19.249L8.5 19.2499C8.49971 19.9389 7.94135 20.499 7.25 20.499V18.499V16.499C5.73044 16.499 4.50065 17.7311 4.5 19.2482L6.5 19.249ZM7.25 18.499V20.499H14.75V18.499V16.499H7.25V18.499ZM17.75 2L17.7478 4C17.887 4.00015 17.9998 4.11299 18 4.25219L20 4.25L22 4.24781C21.9974 1.90296 20.0972 0.00256848 17.7522 1.19209e-06L17.75 2ZM20 4.25H18V8.75H20H22V4.25H20ZM20 8.75L18 8.74651C17.9983 9.70384 17.2284 10.4826 16.2714 10.4953L16.2979 12.4951L16.3243 14.4949C19.4672 14.4533 21.9945 11.8963 22 8.75349L20 8.75ZM16.2979 12.4951L14.5249 11.5696C14.086 12.4102 13.3645 13.0696 12.4874 13.4314L13.25 15.2803L14.0126 17.1292C15.7591 16.4088 17.1964 15.0958 18.0708 13.4206L16.2979 12.4951ZM13.25 15.2803H11.25V16.999H13.25H15.25V15.2803H13.25ZM13.25 16.999V14.999H8.75V16.999V18.999H13.25V16.999ZM8.75 16.999H10.75V15.2803H8.75H6.75V16.999H8.75ZM8.75 15.2803L9.5126 13.4314C8.63551 13.0696 7.91396 12.4102 7.47511 11.5696L5.70215 12.4951L3.92919 13.4206C4.80363 15.0958 6.24088 16.4088 7.9874 17.1292L8.75 15.2803ZM5.70215 12.4951L5.72861 10.4953C4.77159 10.4826 4.00166 9.70384 4 8.74652L2 8.75L2.98023e-06 8.75348C0.00547397 11.8963 2.53275 14.4534 5.67569 14.4949L5.70215 12.4951ZM2 8.75H4V4.25H2H0V8.75H2ZM2 4.25L4 4.25218C4.00015 4.11299 4.113 4.00015 4.25218 4L4.25 2L4.24782 1.19209e-06C1.90284 0.00256073 0.00256085 1.90296 1.19209e-06 4.24782L2 4.25ZM4.25 2V4H17.75V2V0H4.25V2ZM4.24902 3.49902L4.24589 1.49903C2.73037 1.5014 1.50099 2.72948 1.49903 4.24643L3.49902 4.24902L5.49902 4.25162C5.49813 4.94076 4.93988 5.49794 4.25216 5.49902L4.24902 3.49902ZM3.49902 4.24902H1.49902V8.74805H3.49902H5.49902V4.24902H3.49902ZM3.49902 8.74805L1.49903 8.75466C1.5053 10.6488 2.76582 12.3111 4.58951 12.8269L5.13379 10.9023L5.67807 8.97783C5.57303 8.94812 5.49938 8.85163 5.49901 8.74143L3.49902 8.74805ZM5.13379 10.9023L7.08767 10.4753C7.03177 10.2195 7.00199 9.95787 6.99888 9.69514L4.99902 9.71875L2.99916 9.74236C3.00546 10.2755 3.06586 10.8076 3.17991 11.3294L5.13379 10.9023ZM4.99902 9.71875H6.99902V3.49902H4.99902H2.99902V9.71875H4.99902ZM4.99902 3.49902V1.49902H4.24902V3.49902V5.49902H4.99902V3.49902ZM17.001 3.49902H15.001V9.71875H17.001H19.001V3.49902H17.001ZM17.001 9.71875L15.0011 9.69511C14.998 9.95785 14.9682 10.2196 14.9123 10.4753L16.8662 10.9023L18.8201 11.3294C18.9341 10.8076 18.9945 10.2755 19.0008 9.74239L17.001 9.71875ZM16.8662 10.9023L17.4104 12.8269C19.2343 12.3111 20.4947 10.6487 20.501 8.7547L18.501 8.74805L16.501 8.7414C16.5006 8.85174 16.4269 8.94814 16.322 8.97781L16.8662 10.9023ZM18.501 8.74805H20.501V4.24902H18.501H16.501V8.74805H18.501ZM18.501 4.24902L20.501 4.24643C20.499 2.72969 19.2699 1.50122 17.7539 1.49903L17.751 3.49902L17.7481 5.49902C17.06 5.49803 16.5019 4.94049 16.501 4.25162L18.501 4.24902ZM17.751 3.49902V1.49902H17.001V3.49902V5.49902H17.751V3.49902ZM10.9951 4.97363L10.9875 2.97365C9.95464 2.97757 9.04189 3.64086 8.71716 4.61869L10.6152 5.24902L12.5133 5.87935C12.2975 6.52933 11.6902 6.97101 11.0027 6.97362L10.9951 4.97363ZM10.6152 5.24902L8.71678 4.61984L8.20897 6.15206L10.1074 6.78125L12.0059 7.41044L12.5137 5.87821L10.6152 5.24902ZM10.1074 6.78125V4.78125H8.58984V6.78125V8.78125H10.1074V6.78125ZM8.58984 6.78125L8.59105 4.78125C7.56962 4.78063 6.65854 5.42642 6.32164 6.3918L8.20996 7.05078L10.0983 7.70977C9.87403 8.35235 9.26813 8.78166 8.58863 8.78125L8.58984 6.78125ZM8.20996 7.05078L6.32117 6.39316C5.98549 7.35729 6.29571 8.43024 7.09795 9.06575L8.33984 7.49805L9.58173 5.93034C10.1159 6.35351 10.322 7.06724 10.0988 7.7084L8.20996 7.05078ZM8.33984 7.49805L7.09889 9.06649L8.31959 10.0323L9.56055 8.46387L10.8015 6.89542L9.5808 5.9296L8.33984 7.49805ZM9.56055 8.46387L7.65547 7.85503L7.14082 9.46538L9.0459 10.0742L10.951 10.6831L11.4656 9.07271L9.56055 8.46387ZM9.0459 10.0742L7.13994 9.46815C6.82174 10.4688 7.19066 11.56 8.04951 12.1626L9.19824 10.5254L10.347 8.8882C10.9183 9.28905 11.1636 10.0144 10.9519 10.6803L9.0459 10.0742ZM9.19824 10.5254L8.04953 12.1626C8.91273 12.7682 10.0635 12.7371 10.8919 12.1009L9.67383 10.5146L8.45572 8.92839C9.00555 8.50617 9.77129 8.48427 10.347 8.88818L9.19824 10.5254ZM9.67383 10.5146L10.8912 12.1015L12.2164 11.0849L10.999 9.49805L9.78169 7.91119L8.4565 8.92779L9.67383 10.5146ZM10.999 9.49805L9.77773 11.0819L11.0961 12.0985L12.3174 10.5146L13.5387 8.93084L12.2203 7.91424L10.999 9.49805ZM12.3174 10.5146L11.0984 12.1003C11.9289 12.7387 13.0794 12.7664 13.9401 12.1637L12.793 10.5254L11.6458 8.88709C12.2188 8.48585 12.9842 8.5046 13.5363 8.92903L12.3174 10.5146ZM12.793 10.5254L13.9404 12.1635C14.8023 11.5598 15.1686 10.4674 14.8515 9.46882L12.9453 10.0742L11.0391 10.6796C10.8284 10.016 11.0715 9.28936 11.6456 8.88726L12.793 10.5254ZM12.9453 10.0742L14.8506 9.46609L14.3369 7.85672L12.4316 8.46484L10.5263 9.07297L11.04 10.6823L12.9453 10.0742ZM12.4316 8.46484L13.6683 10.0367L14.8959 9.07084L13.6592 7.49902L12.4225 5.92721L11.1949 6.89303L12.4316 8.46484ZM13.6592 7.49902L14.8967 9.07016C15.6953 8.44116 16.0177 7.37057 15.6811 6.3978L13.791 7.05176L11.901 7.70572C11.6763 7.05641 11.892 6.34506 12.4216 5.92789L13.6592 7.49902ZM13.791 7.05176L15.6808 6.39689C15.3457 5.42992 14.4347 4.78183 13.4123 4.78125L13.4111 6.78125L13.41 8.78125C12.7299 8.78086 12.1243 8.35016 11.9013 7.70663L13.791 7.05176ZM13.4111 6.78125V4.78125H11.8916V6.78125V8.78125H13.4111V6.78125ZM11.8916 6.78125L13.7878 6.14517L13.2731 4.611L11.377 5.24707L9.4808 5.88315L9.99545 7.41733L11.8916 6.78125ZM11.377 5.24707L13.2728 4.61001C12.9433 3.62962 12.0248 2.9735 10.9958 2.97363L10.9961 4.97363L10.9963 6.97363C10.3123 6.97372 9.7007 6.53757 9.48113 5.88413L11.377 5.24707ZM10.9961 4.97363V2.97363H10.9951V4.97363V6.97363H10.9961V4.97363Z"
        fill="var(--themeColorNeutral900)"
      />
      <path
        d="M14.75 18.499C15.1641 18.499 15.4998 18.835 15.5 19.249C15.5 19.6632 15.1642 19.999 14.75 19.999H7.25C6.83579 19.999 6.5 19.6632 6.5 19.249C6.50018 18.835 6.8359 18.499 7.25 18.499H14.75ZM17.75 2C18.9921 2.00136 19.9986 3.00798 20 4.25V8.75C19.9964 10.8001 18.3478 12.468 16.2979 12.4951C15.6412 13.753 14.5618 14.7392 13.25 15.2803V16.999H8.75V15.2803C7.4382 14.7392 6.35879 13.753 5.70215 12.4951C3.65217 12.468 2.00357 10.8001 2 8.75V4.25C2.00136 3.00798 3.00792 2.00136 4.25 2H17.75ZM4.24902 3.49902C3.83513 3.49967 3.49956 3.83512 3.49902 4.24902V8.74805C3.50234 9.75021 4.16943 10.6296 5.13379 10.9023C5.04881 10.5135 5.00372 10.1167 4.99902 9.71875V3.49902H4.24902ZM17.001 3.49902V9.71875C16.9963 10.1167 16.9512 10.5136 16.8662 10.9023C17.8306 10.6296 18.4976 9.75023 18.501 8.74805V4.24902C18.5004 3.83509 18.1649 3.49962 17.751 3.49902H17.001ZM10.9951 4.97363C10.8224 4.97429 10.6697 5.08509 10.6152 5.24902L10.1074 6.78125H8.58984C8.41887 6.78115 8.26629 6.88938 8.20996 7.05078C8.15374 7.21227 8.20582 7.39188 8.33984 7.49805L9.56055 8.46387L9.0459 10.0742C8.99268 10.2416 9.05447 10.4245 9.19824 10.5254C9.34201 10.6263 9.53454 10.6216 9.67383 10.5146L10.999 9.49805L12.3174 10.5146C12.4566 10.6217 12.6491 10.6261 12.793 10.5254C12.9369 10.4246 12.9985 10.2417 12.9453 10.0742L12.4316 8.46484L13.6592 7.49902C13.7936 7.39311 13.847 7.21349 13.791 7.05176C13.735 6.89004 13.5823 6.78135 13.4111 6.78125H11.8916L11.377 5.24707C11.322 5.08359 11.1686 4.97361 10.9961 4.97363H10.9951Z"
        fill="var(--colorGold)"
      />
    </svg>
    <!-- <TrophyIcon
      v-else-if="modelValue?.theme === Theme.SanAntonioSpurs"
      class="PlayerAvatar__trophyIcon"
      name="oscarRobertson"
      v-bind="{size}"
    />
    <TrophyIcon
      v-else-if="modelValue?.theme === Theme.NewYorkKnicks"
      class="PlayerAvatar__trophyIcon--larryObrien PlayerAvatar__trophyIcon"
      name="larryObrien"
      v-bind="{size}"
    /> -->
  </component>
</template>

<script lang="ts" setup>
import { supabaseClient } from "@/lib/database/supabase";
import getRouteForPlayer from "@/lib/utils/route/getRouteForPlayer";
import formatPlayerAbbreviation from "@/lib/utils/string/formatPlayerAbbreviation";
import usePlayerStore from "@/stores/usePlayerStore";
import { Theme } from "@shared/lib/constants/themes";
import { PlayerSummary } from "@shared/lib/database/query.types";
import { computed, ref, watch } from "vue";

const player = defineModel<PlayerSummary | null>({
  required: true,
});

const {
  variant = "default",
  size = "medium",
  isHighlighted = false,
  isPlayerLoading = false,
  isClickable = true,
  theme,
  hasAward,
} = defineProps<{
  variant?: "default" | "secondary",
  size?: "medium" | "large",
  theme?: keyof typeof Theme,
  isHighlighted?: boolean,
  isPlayerLoading?: boolean,
  isClickable?: boolean,
  hasAward?: boolean,
}>();

const {
  viewPlayer,
} = usePlayerStore();

const isAvatarImageEnabled = import.meta.env.VITE_IS_AVATAR_IMAGE_ENABLED === "true";
const isImageLoaded = ref(!isAvatarImageEnabled || !player.value?.avatar_storage_path);
const hasImageError = ref(false);
const imageSize = computed(() => size === "medium" ? 36 : 70);
const computedTheme = computed(() => theme || player.value?.theme);

const avatarUrl = computed(() => {
  if (!isAvatarImageEnabled || hasImageError.value || !player.value?.avatar_storage_path) return null;

  const {
    data,
  } = supabaseClient.storage
    .from("avatars")
    .getPublicUrl(player.value.avatar_storage_path, {
      transform: {
        width: imageSize.value * 2,
        height: imageSize.value * 2,
        resize: "cover",
      },
    });

  return data.publicUrl;
});

watch(() => player.value?.avatar_storage_path, () => {
  hasImageError.value = false;
});

const abbreviation = computed(() => {
  return player.value ? formatPlayerAbbreviation(player.value.name) : null;
});

const playerRoute = computed(() => {
  return player.value ? getRouteForPlayer(player.value.id) : null;
});

function handleClick () {
  if (player.value && isClickable) {
    viewPlayer(player.value);
  }
}

function handleImageError (event: Event) {
  const el = (event.target as HTMLImageElement);
  el.style.display = "none";

  hasImageError.value = true;
  isImageLoaded.value = true;
}
</script>

<style lang="scss" scoped>
.PlayerAvatar {
  --avatarSize: 48px;
  --fontSize: 18px;
  --highlightSize: 0px;
  --transition: 250ms var(--easeOutBack);
  --transitionScale: calc((var(--avatarSize) - 4px) / var(--avatarSize));

  position: relative;

  flex: 0 0 auto;
  width: var(--avatarSize);

  color: var(--themeColorWhite);

  font: var(--fontWeightMedium) var(--fontSize) / 1 var(--fontFamilyNormal);

  &[data-size="large"] {
    --avatarSize: 88px;
    --fontSize: 32px;
  }

  &[aria-busy="true"]::after {
    opacity: 0;
  }
}

.PlayerAvatar--isHighlighted {
  --highlightSize: 3px;
}

.PlayerAvatar__container {
  position: relative;

  display: grid;
  place-items: center;
  aspect-ratio: 1;

  border-radius: 50%;
  background-color: var(--themeColorSurface);

  .PlayerAvatar--isHighlighted & {
    border: 3px solid var(--themeColorNeutral0);
  }
}

.PlayerAvatar__separator {
  position: absolute;
  inset: calc(-1px - var(--highlightSize));

  border: 3px solid var(--themeColorWhite);
  border-radius: inherit;

  transition: opacity var(--transition);

  [data-theme="dark"] & {
    border-color: var(--themeColorSurface);
  }

  .PlayerAvatar:not(.PlayerAvatar--isPlaceholder) & {
    opacity: 0;
  }
}

.PlayerAvatar__background {
  position: absolute;
  inset: 2px;

  border-radius: inherit;
  background-color: var(--themeColorNeutral200);

  transition: transform var(--transition);

  [data-theme="dark"] & {
    background-color: var(--themeDarkTransparent200);
  }

  .PlayerAvatar:not(.PlayerAvatar--isPlaceholder) & {
    transform: scale(calc(1 / var(--transitionScale)));
  }
}

.PlayerAvatar__border {
  position: absolute;
  inset: 0;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: inherit;

  transition: var(--transition);
  transition-property: opacity, transform;

  .PlayerAvatar--isPlaceholder & {
    transform: scale(var(--transitionScale));
    opacity: 0;
  }
}

.PlayerAvatar__imageContainer,
.PlayerAvatar__abbreviationContainer {
  transition: var(--transition);
  transition-property: opacity, transform;

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(var(--transitionScale));
    opacity: 0;
  }
}

.PlayerAvatar__abbreviationContainer {
  position: absolute;
  inset: 0;

  border: solid 6px var(--themeColorSecondary);
  border-radius: inherit;
  background-color: var(--themeColorPrimary);

  transition: var(--transition);
  transition-property: border-color, background-color;
}

.PlayerAvatar__imageContainer {
  position: absolute;
  inset: 0;

  display: grid;
  place-items: center;
  overflow: hidden;

  border-radius: inherit;
  background-color: var(--themeColorPrimary);

  transition: background-color var(--transition);

  .PlayerAvatar[data-variant="secondary"] & {
    background-color: var(--themeColorSecondary);
  }

  &::before {
    position: absolute;
    inset: -6px -6px 50%;
    transform: rotate(-45deg);
    transform-origin: bottom;

    border-radius: 9999px 9999px 0 0;
    background-color: var(--themeColorSecondary);
    opacity: 0;

    transition: background-color var(--transition);

    content: "";
  }

  .PlayerAvatar--hasImage &::before {
    opacity: 1;
  }
}

.PlayerAvatar__image {
  position: relative;
  display: block;
  border-radius: 50%;
}

.PlayerAvatar__abbreviation {
  position: absolute;
  inset: 0;

  display: grid;
  place-items: center;
  padding-top: var(--fontSafariOffset);
}

.PlayerAvatar__awardIcon {
  position: absolute;
  right: -2px;
  bottom: 0;
}

.PlayerAvatar__trophyIcon {
  position: absolute;
  right: 0;
  bottom: 0;
}

.PlayerAvatar__trophyIcon--larryObrien {
  right: -7px;
  bottom: 0;
}
</style>
